<template>
  <ValidationProvider
    v-slot="v"
    :name="attribute"
    :rules="rules"
    :custom-messages="ruleMessages"
    tag="div"
  >
    <b-form-group
      v-if="
        isToggleSwitch()
          || isInput()
          || isTextarea()
          || isRadioGroup()
          || isDatetime()
          || isDate()
          || isSelect()
          || isTagsInput()
          || isMultiSelect()
          || isHierarchySelect()
          || isHierarchyList()
          || isHierarchyTree()
          || isSearchInput()
          || isSelectWithFilter()
          || isCustomSelect()
          || isDocument()
      "
    >
      <div class="row">
        <div
          v-if="label"
          class="col-lg-6 input-label"
          :class="[align === 'column' ? 'col-lg-12' : 'col-lg-6']"
        >
          <label>
            {{ label }}
            <span
              v-if="v.required"
              class="text-danger"
            >*</span>
            <span 
              v-if="validationWarnings?.length"
              class="ml-1 cursor-pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'circle-xmark']"
                size="lg"
                class="text-danger"
                data-toggle="tooltip"
                title="Validation Failed"
              />
            </span>
            <span 
              v-if="dataQualityIssues?.length"
              class="ml-1 cursor-pointer"
            >
              <font-awesome-icon
                :icon="['fas', 'circle-info']"
                size="lg"
                class="text-warning"
                data-toggle="tooltip"
                title="Data Quality Issue"
              />
            </span>
          </label>
          <div>
            <ul class="small m-0">
              <li 
                v-for="valItem in validationWarnings"
                class="my-1 p-1 alert alert-danger"
              >
                <strong>{{ valItem.warning }}</strong><span v-if="valItem.description"> - {{ valItem.description }}</span>
              </li>
            </ul>
          </div>
          <div>
            <ul class="small m-0">
              <li 
                v-for="valItem in dataQualityIssues"
                class="my-1 p-1 alert alert-warning"
              >
                <strong>{{ valItem.warning }}</strong><span v-if="valItem.description"> - {{ valItem.description }}</span> 
              </li>
            </ul>
          </div>
        </div>
        <div :class="[align === 'column' ? 'col-lg-12' : 'col-lg-6']">
          <b-input-group :prepend="prepend">
            <search-input
              v-if="isSearchInput()"
              :key="`${attribute}-${uuid}`"
              v-model="model[attribute]"
              :suggestions="field.suggestions"
              :focus="autofocus"
              :placeholder="placeholder"
              :state="getState(v)"
              @change="onChange"
              @click="onClick"
              @input="onInput"
              @keydown="onKeydown"
              @blur="onBlur"
            />
            <b-form-input
              v-else-if="isInput() && !rls"
              :id="id"
              ref="currentInput"
              :key="`${attribute}-${uuid}`"
              v-model="model[attribute]"
              :type="type"
              :disabled="disabled"
              :name="attribute"
              :autofocus="autofocus"
              :state="getState(v)"
              :placeholder="placeholder"
              :step="isFieldDecimal ? 'any' : null"
              :class="warningMessage ? 'form-control-warning' : null"
              @change="onChange"
              @click="onClick"
              @input="onInput"
              @keydown="onKeydown"
              @blur="onBlur"
            />
            <div 
              v-else-if="rls"
              class="rls"
              @click="triggerRLSIconClick"
              :title="rlsGetUsersForGuids(model[attribute])"
            >
              <user-list-popover
                :key="`${attribute}-${uuid}`"
                ref="user-popover"
                v-model="model[attribute]"
                :selected-users="JSON.parse(model[attribute] || '[]')"
                :is-single-page="false"
                :component-id="`row-${uuid}`"
                :render-button="false"
                @userSelect="rlsUserSelect"
                @userRemove="rlsUserRemove"
              />
              {{ rlsGetUsersForGuids(model[attribute]) }}
            </div>
            <custom-date-picker
              v-if="isDate() || isDatetime()"
              :id="id"
              :ref="`currentInput-${attribute}`"
              :key="`${attribute}-${uuid}`"
              v-model="model[attribute]"
              class="datepicker-w"
              :type="`${isDate() ? 'date' : 'datetime'}`"
              :input-class="`form-control ${hasFocus ? 'datepicker-validation' : '' }`"
              :placeholder="placeholder"
              :disabled="field.disabled || disabled"
              :format="dateFormat"
              :value-type="dateFormat"
              :name="`${attribute}-${uuid}`"
              :open="customDateChange[`open_${attribute}`]"
              :state="getState(v)"
              @focus="onFocus"
              @change="onChange"
              @close="onDatePickerClose"
            />
            <!-- Document field type input area -->
            <div
              v-if="isDocument()"
              :id="id"
              ref="currentInput"
              :disabled="disabled"
              :name="`${attribute}-${uuid}`"
              :state="getState(v)"
              readonly
              class="w-100"
            >
              <!-- Hidden input for VeeValidate required rule -->
              <input
                type="hidden"
                :name="`${attribute}-${uuid}`"
                :value="documentFiles.length ? 'has-files' : ''"
                :required="field.required || field.isRequired"
              >
              <!-- Input area with badges or placeholder text -->
              <div class="d-flex flex-row w-100 mb-2">
                <div 
                  class="d-flex flex-wrap align-items-center justify-content-start document-input-div"
                  :class="documentFiles.length ? 'bg-white' : ''"
                >
                  <span v-if="documentFiles.length">
                    <b-badge
                      v-for="(file, index) in documentFiles"
                      class="d-inline-flex align-items-center mr-2 my-1 document-badge"
                      variant="light"
                    >
                      <font-awesome-icon
                        :icon="getFileTypeIcon(file.name)"
                        class="mr-2"
                        :style="getFileTypeColour(file.name)"
                      />
                      <span 
                        :id="`document-badge-${index}`"
                        class="file-name-text"
                      >{{ file.name }}
                      </span>
                      <b-btn-close
                        :id="`remove-file-btn-${index}`"
                        class="ml-2"
                        :disabled="field.disabled || disabled"
                        @click.stop="removeFile(index)"
                      />
                      <b-tooltip
                        :target="`document-badge-${index}`"
                        placement="bottom"
                        triggers="hover"
                        :title="file.name"
                      />
                    </b-badge>
                  </span>
                  <span v-else>No files selected</span>
                </div>
                <!-- Button is next to input when no files -->
                <b-button
                  v-if="!documentFiles.length"
                  id="select-files-btn"
                  variant="primary"
                  :disabled="field.disabled || disabled"
                  class="ml-1"
                  @click="showSharePointModal = true"
                >
                  Select File(s)
                </b-button>
              </div>
              
              <!-- Button is below input when there are files -->
              <div
                v-if="documentFiles.length"
                class="d-flex justify-content-start"
              >
                <b-button
                  id="select-more-files-btn"
                  variant="primary"
                  :disabled="field.disabled || disabled"
                  @click="showSharePointModal = true"
                >
                  Select More Files
                </b-button>
              </div>

              <share-point-selector-modal 
                v-if="showSharePointModal"
                :initial-location-data="field.dataTypeJSON"
                :is-selecting-files="true"
                title="Select File(s)"
                @document-selected="onDocumentSelected"
                @hidden="showSharePointModal = false"
              />
            </div>
            <b-form-invalid-feedback 
              v-if="customFieldErrors.document.maxFilesError.show"
              :state="false"
            >
              {{ customFieldErrors.document.maxFilesError.message }}
            </b-form-invalid-feedback>
          </b-input-group>
          <toggle-switch
            v-if="isToggleSwitch()"
            v-model="model[attribute]"
            :options="toggleOptions"
            :group="`-${id}`"
          />
          <b-form-textarea
            v-if="isTextarea()"
            :id="id"
            ref="currentInput"
            :key="`${attribute}-${uuid}`"
            v-model="model[attribute]"
            :type="type"
            max-rows="6"
            class="overflow-y-auto"
            :disabled="disabled"
            :name="`${attribute}-${uuid}`"
            :readonly="readonly"
            :state="getState(v)"
            :placeholder="placeholder"
            :autofocus="autofocus"
            @change="onChange"
          />
          <b-form-tags
            v-if="isTagsInput()"
            ref="currentInput"
            :key="`${attribute}-${uuid}`"
            v-model="model[attribute]"
            :type="type"
            :disabled="disabled"
            :name="`${attribute}-${uuid}`"
            :state="getState(v)"
            :placeholder="placeholder"
            :autofocus="autofocus"
          />
          <b-form-radio-group
            v-if="isRadioGroup()"
            :id="`radio-group-${attribute}-${uuid}`"
            :key="`${attribute}-${uuid}`"
            v-model="model[attribute]"
            :class="{'d-flex': align === 'row'}"
            :options="options"
            :state="getState(v)"
            :disabled="disabled"
            :name="`${attribute}-${uuid}`"
          />
          <template v-if="isSelect()">
            <b-form-select
              :id="selectInputId"
              :key="`${attribute}-${uuid}`"
              v-model="model[attribute]"
              :options="options"
              :disabled="disabled"
              :state="getState(v)"
              :name="`${attribute}-${uuid}`"
              @change="onChange"
            />
          </template>
          <select
            v-if="isCustomSelect()"
            :id="selectInputId"
            :key="`${attribute}-${uuid}`"
            v-model="model[attribute]"
            :name="`${attribute}-${uuid}`"
            :disabled="disabled"
            :state="getState(v)"
            class="mr-sm-2 custom-select"
            @change="onChange($event)"
          >
            <option
              v-for="option in options"
              :id="$options.filters.displayName(option.text)"
              :value="option.value"
            >
              {{ option.text }}
            </option>
          </select>
          <multiselect
            v-if="isMultiSelect()"
            :id="selectInputId || `multi-select-${attribute}`"
            :key="`${attribute}-${uuid}`"
            v-model="model[attribute]"
            :options="getOptions(model, field)"
            :name="`${attribute}-${uuid}`"
            :track-by="config.optionValue"
            :label="config.optionLabel"
            :placeholder="config.placeholder"
            :multiple="config.multiple"
            :disabled="disabled"
            :close-on-select="config.closeOnSelect"
            :show-labels="false"
            :loading="isLookupFieldValuesLoading"
            :taggable="config.taggable"
            @tag="onAddNewTag"
            @input="onSelectItem"
            @open="onMultiselectOpen(attribute, model, field)"
            @search-change="(query) => searchWithDebounce(query, field, 500)"
          >
            <template slot="beforeList">
              <b-button
                v-if="!isLookupFieldValuesLoading && field.isTable"
                size="sm"
                variant="light"
                class="w-100 text-left"
                @click="onAddNewRelationalEntity(model, field)"
              >
                <font-awesome-icon :icon="['fas', 'plus']" />
                {{ getTableName(field, model) }}
              </b-button>
              <div
                v-else
                class="pl-3"
              >
                Loading...
              </div>
            </template>
            <template slot="noResult">
              <span>No elements found</span>
            </template>
            <template
              v-if="!isLookupFieldValuesLoading && field.isTable"
              slot="option"
              slot-scope="props"
            >
              <span :id="`multiselect-option-${props.option[config.optionValue]}`">
                {{ props.option[config.optionLabel] }}
              </span>
            </template>
            <template
              v-if="!isLookupFieldValuesLoading && field.data.options && field.data.options.length >= 50"
              slot="afterList"
            >
              <div
                class="py-1 px-2 text-button text-justify"
                style="font-size: 10px; text-wrap: wrap;"
              >
                The list above displays the first 50 lookup values. If you're looking for a specific value,
                please enter populate search box to find them.
              </div>
            </template>
          </multiselect>

          <b-form-group v-if="isHierarchyTree()">
            <treeselect
              v-model="model[attribute]"
              :normalizer="normalizer"
              :multiple="config.multiple"
              :options="options.data"
              :close-on-select="closeTreeSelect"
              :disabled="disabled"
              @close="onCloseEvent"
              @select="onSelectValue"
            >
              <label
                slot="option-label"
                slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }"
                :class="{ 'disabled': node.raw.$isDisabled, [labelClassName]: labelClassName }"
              >
                {{ node.label }}
              </label>
            </treeselect>
            <b-form-invalid-feedback :state="getHierarchySelectState()">
              {{ error }}
            </b-form-invalid-feedback>
          </b-form-group>

          <b-form-group v-if="isHierarchyList()">
            <div class="row">
              <div
                v-for="(value, level) of options.levels"
                class="col mt-2"
              >
                {{ value.levelName }}
                <b-list-group class="hierarchy-list-group">
                  <b-list-group-item>
                    <b-form-input
                      v-model="hierarchySearch[level]"
                      :placeholder="`Filter ${value.levelName}`"
                      type="text"
                    />
                  </b-list-group-item>

                  <b-list-group-item
                    v-for="item of getHierarchyListItems(level)"
                    :key="`hierarchy-list-${item[config.optionValue]}`"
                    :active="item[config.optionValue] === model[attribute][level] && (!value.disabled)"
                    :disabled="value.disabled || disabled"
                    @click="setHierarchyValue(item[config.optionValue], level)"
                  >
                    {{ item[config.optionLabel] }}
                  </b-list-group-item>

                  <div class="message-container">
                    <span
                      v-if="getHierarchyListItems(level).length === 0 || displaySetMessage"
                      class="centered-message"
                    >
                      {{ getEmptyMessage(level) }}
                    </span>
                  </div>
                </b-list-group>
              </div>
              <b-form-invalid-feedback
                :state="getHierarchySelectState()"
                class="col-12"
              >
                {{ error }}
              </b-form-invalid-feedback>
              <div
                v-if="Object.keys(options.levels).length === 0"
                class="col"
              >
                <span class="centered-message">
                  Items not found
                </span>
              </div>
            </div>
          </b-form-group>

          <!--Need to find better way to rerender model and display updated values instead of adding !displaySetMessage-->
          <b-form-group v-if="isHierarchySelect() && !displaySetMessage">
            <div class="row">
              <div
                v-for="(value, level) of options.levels"
                class="col"
              >
                {{ value.levelName }}
                <multiselect
                  :id="`hierarchy-select-${attribute}-${level}`"
                  :key="`${attribute}-${uuid}-${level}`"
                  v-model="model[attribute][level]"
                  :options="options.optionByLevel[level]"
                  :track-by="config.optionValue"
                  :label="config.optionLabel"
                  :disabled="disabled"
                  :name="`${attribute}-${uuid}-${level}`"
                  @input="setHierarchyValue(model[attribute][level], level)"
                />
              </div>


              <b-form-invalid-feedback
                :state="getHierarchySelectState()"
                class="col-12"
              >
                {{ error }}
              </b-form-invalid-feedback>

              <div v-if="isHierarchyEmpty()">
                <div class="col">
                  <span class="centered-message"> No items found </span>
                </div>
              </div>
            </div>
          </b-form-group>
          <b-form-invalid-feedback :state="getState(v)">
            {{ customDateChange.isInvalidDate ? `Date format must be ${dateFormat}` : getError(v.errors) }}
          </b-form-invalid-feedback>
          <small
            v-if="warningMessage"
            class="warning-feedback"
          >
            {{ warningMessage }}
          </small>
          <b-form-text v-if="hint">
            {{ hint }}
          </b-form-text>
        </div>
      </div>
    </b-form-group>


    <b-form-group
      v-else-if="isCheckbox()"
      :label="labelPosition === 'top' ? label : null"
    >
      <b-form-checkbox
        :id="id"
        ref="currentInput"
        :key="`${attribute}-${uuid}`"
        v-model="model[attribute]"
        :disabled="disabled"
        :name="`${attribute}-${uuid}`"
        :switch="isSwitch"
        :class="field.class"
        :data-field-guid="field.fieldGuid"
        :state="getState(v)"
        @change="onChange"
      >
        <span v-if="label && labelPosition === 'right'">
          {{ label }}
          <span
            v-if="v.required"
            class="text-danger"
          >
            *
          </span>
        </span>
      </b-form-checkbox>
      <b-form-invalid-feedback :state="getState(v)">
        {{ getError(v.errors) }}
      </b-form-invalid-feedback>
    </b-form-group>

    <div class="select-with-filter-group">
      <b-form-group>
        <v-select
          v-if="isSelectWithFilter()"
          :key="`${attribute}-${uuid}`"
          v-model="model[attribute]"
          :options="field.data.options"
          :disabled="disabled"
          :name="`${attribute}-${uuid}`"
          :reduce="option => option.value"
          :selectable="option => option.value !== null"
          label="text"
        />
      </b-form-group>
    </div>
  </ValidationProvider>
</template>

<script>
import {uuid} from 'vue-uuid';
import {createNamespacedHelpers} from 'vuex';
import moment from 'moment';
import _ from 'lodash';
import {axiosClient} from '@/core/services/http.service';
import { getFileTypeIcon, getFileTypeColour } from '@/core/utils/fileTypeUtils';
// Components
import ToggleSwitch from 'vue-on-off-switch';
import UserListPopover from '@/modules/DataManagement/components/UserListPopover';
import SharePointSelectorModal from '@/modules/DataManagement/components/SharePointSelectorModal.vue';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
const {mapActions: mapActionsUser} = createNamespacedHelpers('user');
const {mapState: sidebarMapState} = createNamespacedHelpers('sidebar');
export default {
  name: 'InputWidget',
  components: {ToggleSwitch, UserListPopover, SharePointSelectorModal},
  props: {
    model: {
      type: Object,
      default() {
        return {};
      },
    },
    field: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    isSwitch: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    prepend: {
      type: [String, Number],
      default: null,
    },
    max: {
      type: [String, Number],
      default: 45,
    },
    error: {
      type: String,
      required: false,
    },
    dateFormat: {
      type: String,
      required: false,
    },
    skipValidationRules: {
      type: Boolean,
      default: false,
    },
    toggleOptions: {
      type: Object,
    },
    align: {
      type: String,
      default: 'column',
    },
    rls:{
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rlsUsers:[],
      intl: null,
      isLookupFieldValuesLoading: false,
      hasFocus: false,
      value: null,
      displaySetMessage: false,
      attribute: '',
      label: '',
      labelPosition: 'right',
      placeholder: '',
      hint: '',
      rules: '',
      type: 'text',
      options: [],
      uuid: uuid.v4(),
      config: {},
      hierarchySearch: {},
      closeTreeSelect: true,
      urlError: '',
      ruleMessages: {
        required: 'This field is required',
        email: 'The email field must be a valid email',
        max_value: function (name, rule) {
          return 'The field must be 9999999 or less';
        },
        min_value: function (name, rule) {
          return 'The field must be 1 or more';
        },
      },
      customFieldErrors: {
        number: {
          decimalValueError: {
            show: false,
            message: 'Value must be whole number, decimal entered',
          },
        },
        decimal: {
          tooManyDecimalPlaceError: {
            show: false,
            message: 'Decimal places exceeds the allowed number of decimal places',
          },
        },
        document: {
          maxFilesError: {
            show: false,
            message: '',
          },
        },
      },
      searchChange: function () {
      },
      customDateChange: {
        inputDate: '',
        inputEdit: false,
        isInvalidDate: false,
      },
      saveSearchMapForDebounce: new Map(),
      backupField: {},
      firstOptions: [],
      validationWarnings: null,
      dataQualityIssues: null,
      showSharePointModal: false,
      documentFiles: [],
    };
  },
  watch: {
    ['field']() {
      this.initialWidgetData();
    },
  },
  computed: {
    ...mapState({
      currentTable: state => state.tableData.data,
      fieldValues: state => state.fieldValues,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    normalizer() {
      const vm = this;
      return function (node) {
        return {
          id: node[vm.config.optionValue],
          label: node[vm.config.optionLabel],
          children: node.children,
        };
      };
    },
    isFieldDecimal() {
      return this.field.dataTypeName === 'decimalNumber' || this.field.dataTypeName === 'floatNumber';
    },
    selectInputId() {
      return this.id || `select-${this.attribute}`;
    },
    warningMessage() {
      if (!this.attribute || !this.field.dataTypeJSON || !this.model[this.attribute]) {
        return null;
      }
      const jsonData = JSON.parse(this.field.dataTypeJSON);
      const value = this.model[this.attribute].toString();

      const decimalArray = value.split('.');
      const scale = jsonData.Scale;

      if (jsonData.ApplyRounding && decimalArray.length > 1 && decimalArray[decimalArray.length - 1].length > scale) {
        return 'Number of decimals has exceeded the allowed amount and will be trimmed on submission to the appropriate number of decimal places allowed as per the field set up.';
      }
      return null;
    },
    isFieldLookup() {
      return this.field.dataTypeName === this.fieldValues.lookup;
    },
  },
  methods: {
    ...mapActions(['getPagedDataLookupValueItems']),
    ...mapActionsUser(['getUsersAndGroupsForIds']),
    async searchWithDebounce(field, query, debounceMS = 1000) {
      let timeout = this.saveSearchMapForDebounce.get(field.fieldGuid);

      if (timeout) {
        clearTimeout(timeout);
        this.saveSearchMapForDebounce.delete(field.fieldGuid);
      }

      timeout = setTimeout(async () => {
        await this.lookupOptionsFind(field, query);
        clearTimeout(timeout);
      }, debounceMS);

      this.saveSearchMapForDebounce.set(field.fieldGuid, timeout);
    },
    triggerRLSIconClick() {
      var el = document.getElementById(`usersPopoverButtonrow-${this.uuid}`);
      if (el) {
        const event = new MouseEvent('click', {
          view: window,
          bubbles: true,
          cancelable: true,
        });
        el.dispatchEvent(event);
      }
    },
    rlsUserSelect(user){
      const u = {userGuid: user.id, displayName: user.displayName};
      this.rlsUsers.push(u);
      const currentUsers = JSON.parse(this.model[this.attribute] || '[]');
      currentUsers.push(u.userGuid);
      this.model[this.attribute] = JSON.stringify(currentUsers);
    },
    rlsUserRemove(user) {
      const currentUsers = JSON.parse(this.model[this.attribute] || '[]');
      const updatedUsers = currentUsers.filter(u => u !== user.id);
      this.model[this.attribute] = JSON.stringify(updatedUsers);
    },
    rlsGetUsersForGuids(value){
      try {
        if(this.rls && this.rlsUsers.length){
          const guids = JSON.parse(value || '[]');
          return guids
            .map(guid => {
              const match = this.rlsUsers.find(user => user.userGuid === guid);
              return match ? match.displayName : guid;
            })
            .join(', ');
        }
      } catch (error) {
        console.error('Failed to parse or map user GUIDs:', error);
        return '';
      }
    },
    formatDecimal(number, decimalPlaces) {
      const numString = number.toString();

      const [integerPart, decimalPart] = numString.split('.');

      const formattedDecimalPart = decimalPart
        ? decimalPart.padEnd(decimalPlaces, '0')
        : '0'.repeat(decimalPlaces);

      return `${integerPart || '0'}.${formattedDecimalPart}`;
    },
    async lookupOptionsFind(query, field) {
      this.isLookupFieldValuesLoading = true;
      const {body} = await this.getPagedDataLookupValueItems({field, query});
      const mdmDataTypeJSON = JSON.parse(body.mdmDataTypeJSON);
      const dataTypeName = mdmDataTypeJSON.UnderlyingDataType.split('.')[1];

      const sourceBackupField = JSON.parse(JSON.stringify(this.backupField));
      const sourceOptions = sourceBackupField.data.options;

      let foundOptions;

      if (dataTypeName === 'Decimal') {
        const scale = mdmDataTypeJSON.Scale;
        foundOptions = [...body.data.map(val => ({
          id: val.recordGuid,
          text: this.formatDecimal(val.value, scale).split(',').join(''),
        }))];
      } else {
        foundOptions = [...body.data.map(val => ({id: val.recordGuid, text: val.value}))];
      }

      const combinedOptions = sourceOptions.concat(foundOptions);
      const uniqCombinedOptions = _.uniqBy(combinedOptions, 'id');
      // uniqCombinedOptions.slice(0, 50);
      uniqCombinedOptions.sort((a, b) => a.text - b.text);

      field.data.options = JSON.parse(JSON.stringify(uniqCombinedOptions));
      this.isLookupFieldValuesLoading = false;
    },
    onSelectItem(val) {
      this.$emit('select', val);
    },
    onSelectValue(node) {
      this.closeTreeSelect = true;
      if (node.$isDisabled) {
        this.closeTreeSelect = false;
        this.model[this.attribute] = null;
      }
    },
    onCloseEvent() {
      this.closeTreeSelect = true;
    },
    getError(errors) {
      if (this.urlError) {
        return this.urlError;
      }

      if (this.customFieldErrors.number.decimalValueError.show) {
        return this.customFieldErrors.number.decimalValueError.message;
      }

      if (this.customFieldErrors.decimal.tooManyDecimalPlaceError.show) {
        return this.customFieldErrors.decimal.tooManyDecimalPlaceError.message;
      }

      if (this.customFieldErrors.document.maxFilesError.show) {
        return this.customFieldErrors.document.maxFilesError.message;
      }

      return errors[0];
    },
    getState(validator) {
      if (this.error) {
        return false;
      }

      const isFloat = (n) => Number(n) === n && n % 1 !== 0;

      // Validate date format
      if (this.customDateChange.inputEdit) {
        return this.customDateChange.isInvalidDate ? false : null;
      }

      // Check url validation
      if (this.field.type === 'url' && this.model[this.attribute] !== '') {
        const pattern = new RegExp('^(https?:\\/\\/)?' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
        this.urlError = !pattern.test(this.model[this.attribute]) ? 'This URL is not validate' : '';
        return !!pattern.test(this.model[this.attribute]);
      }

      if (this.field.dataTypeName === this.fieldValues.wholeNumber && isFloat(parseFloat(this.model[this.attribute]))) {
        this.customFieldErrors.number.decimalValueError.show = true;
        return false;
      } else {
        this.customFieldErrors.number.decimalValueError.show = false;
      }

      if ((this.field.dataTypeName === this.fieldValues.decimalNumber || this.field.dataTypeName === this.fieldValues.floatNumber) && this.model[this.attribute]) {

        const jsonData = JSON.parse(this.field.dataTypeJSON);
        const value = this.model[this.attribute].toString();

        const decimalArray = value.split('.');
        const scale = jsonData.Scale;

        if (!jsonData.ApplyRounding && decimalArray.length > 1 && decimalArray[decimalArray.length - 1].length > scale) {
          this.customFieldErrors.decimal.tooManyDecimalPlaceError.show = true;
          return false;
        }

      } else {
        this.customFieldErrors.number.decimalValueError.show = false;
      }

      // Validate is set when validate function of vee validate is run
      if (validator.validated === true) {
        // return validator.valid;
        return validator.valid === true ? null : false;
      }

      if (!validator.touched) {
        return null;
      }

      // return !validator.touched ? null : validator.valid
      return validator.valid === true ? null : false;
    },
    getRules() {
      const rules = [];
      if (this.field.required || this.field.isRequired) {
        rules.push('required');
      }
      if (this.field.type === 'email') {
        rules.push('email');
      }
      if (this.field.type === 'number') {
        if (this.field.min !== undefined) {
          rules.push(`min_value:${this.field.min}`);
        }
        if (this.field.max !== undefined) {
          rules.push(`max_value:${this.field.max}`);
        }
      }
      if (this.field.type === 'text') {
        if (this.field.data && this.field.data.min) {
          rules.push(`min:${this.field.data.min}`);
        }
        if (this.field.data && this.field.data.max) {
          rules.push(`max:${this.field.data.max}`);
        }
      }
      if (this.field.type === 'date') {

      }

      return rules.join('|');
    },
    getHierarchySelectState() {
      return !!!this.error;

      if (parseInt(level) <= this.field.testMinLevel && (!this.model[this.attribute][level] || parseInt(this.model[this.attribute][level]) === -1)) {
        return false;
      }

      return true;

    },
    isToggleSwitch() {
      return this.type === 'toggle-switch';
    },
    isInput() {
      return ['text', 'number', 'password', 'email', 'search', 'url', 'tel', 'time', 'range', 'color'].includes(this.type);
    },
    isSearchInput() {
      return this.type === 'search-input';
    },
    isTextarea() {
      return this.type === 'textarea';
    },
    isCheckbox() {
      return this.type === 'checkbox';
    },
    isSelect() {
      return this.type === 'select';
    },
    isMultiSelect() {
      return this.type === 'multi-select';
    },
    isCustomSelect() {
      return this.type === 'custom-select';
    },
    isTagsInput() {
      return this.type === 'tags';
    },
    isRadioGroup() {
      return this.type === 'radio-group';
    },
    isDatetime() {
      return this.type === 'datetime';
    },
    isDate() {
      return this.type === 'date';
    },
    isHierarchySelect() {
      return this.type === 'hierarchy-select';
    },
    isHierarchyList() {
      return this.type === 'hierarchy-list';
    },
    isHierarchyTree() {
      return this.type === 'hierarchy-tree';
    },
    isHierarchyEmpty() {
      return Object.keys(this.options.levels).length === 0;
    },
    isSelectWithFilter() {
      return this.type === 'select-with-filter';
    },
    isDocument() {
      return this.field.dataTypeName === 'document';
    },
    onChange(val) {
      if (this.type === 'number' && val === '') {
        this.model[this.attribute] = null;
      }

      this.$emit('change', val);
      this.$emit('input', val);
      this.hasFocus = !val;
    },
    /** Methods for document field types */
    /**
     * Handle document selection from SharePoint modal
     * Validates file count limits and adds new files to the document list
     * @param {Array} files - Array of selected files from SharePoint
     */
    onDocumentSelected(files) {
      // Clear any previous error
      this.customFieldErrors.document.maxFilesError.show = false;
      this.customFieldErrors.document.maxFilesError.message = '';
      
      // Filter out files that are already in the documentFiles array to avoid duplicates
      const newFiles = files.filter(newFile => 
        !this.documentFiles.some(existingFile => existingFile.id === newFile.id),
      );
      
      // Check if adding new files would exceed the maximum allowed files
      const currentCount = this.documentFiles.length;
      const newCount = currentCount + newFiles.length;
      
      // Get max files from field configuration
      let maxFiles = 999; // Default value
      if (this.field.dataTypeJSON) {
        try {
          const config = JSON.parse(this.field.dataTypeJSON);
          if (config.MaxFilesPerRow) {
            maxFiles = parseInt(config.MaxFilesPerRow, 10);
          }
        } catch (error) {
          return; // If parsing fails, use default
        }
      }
      
      // If new count exceeds maxFiles, show error and don't add files
      if (newCount > maxFiles) {
        this.customFieldErrors.document.maxFilesError.show = true;
        this.customFieldErrors.document.maxFilesError.message = `Maximum ${maxFiles} ${maxFiles === 1 ? 'file' : 'files'} allowed. Cannot add ${newFiles.length} more ${newFiles.length === 1 ? 'file' : 'files'}.`;
        return; 
      }
      
      // Add the new files
      this.documentFiles = [...this.documentFiles, ...newFiles];
      this.model[this.attribute] = this.documentFiles;
    },
    /**
     * Remove a file from the document list by index
     * @param {number} index - Index of the file to remove
     */
    removeFile(index) {
      // Create a copy and remove the file at the specified index
      const updatedFiles = [...this.documentFiles];
      updatedFiles.splice(index, 1);
      this.documentFiles = updatedFiles;
      this.model[this.attribute] = this.documentFiles;
      
      // Clear any max files error when removing files
      this.customFieldErrors.document.maxFilesError.show = false;
      this.customFieldErrors.document.maxFilesError.message = '';
    },
    // Get Font Awesome icon based on file extension
    getFileTypeIcon(fileName) {
      return getFileTypeIcon(fileName);
    },
    // Get the colour style for file type icons
    getFileTypeColour(fileName) {
      return getFileTypeColour(fileName);
    },
    onInput($event) {
      this.$emit('input', $event);
    },
    onClick($event) {
      this.$emit('click', $event);
    },
    onFocus($event) {
      if (this.isDate() || this.isDatetime()) {
        this.addDatePickerEventListeners($event);
      }
      this.$emit('focus', $event);
    },
    onDatePickerClose() {
      this.hasFocus = !this.model[this.attribute];
      this.customDateChange = {
        ...this.customDateChange,
        inputEdit: false,
        inputDate: '',
        [`open_${this.attribute}`]: false,
      };
    },
    onBlur($event) {
      this.$emit('blur', $event);
    },
    onKeydown($event) {
      this.$emit('keydown', $event);
    },
    getOptions(item, field) {
      const suggestFieldName = item[`${field.name}__$LookupStatus`];
      if (suggestFieldName && suggestFieldName.options && suggestFieldName.options.length > 0) {
        const uniqueObjects = [...suggestFieldName.options, ...field.data.options].reduce((acc, obj) => {
          if (!acc.some(item => item.text === obj.text) && !('value' in obj)) {
            acc.push(obj);
          }
          return acc;
        }, []);
        return uniqueObjects;
      } else {
        return field.data.options;
      }
    },
    onAddNewTag(e) {
      this.$emit('tag', e);
    },
    async onMultiselectOpen(index, item, field) {
      if (this.isFieldLookup) {
        this.isLookupFieldValuesLoading = true;
        if(
          !('relationalTableId' in field && field.relationalTableId)
            && !('relationalFieldId' in field && field.relationalFieldId)
        ) {
          const id = JSON.parse(field.dataTypeJSON).FieldToDisplayGuid;
          const  relatedFieldItem = await axiosClient.get(`api/field/${id}`);
          field.relationalTableId = relatedFieldItem.body.entityGuid;
          field.relationalFieldId = id;
        }
        const {body} = await this.getPagedDataLookupValueItems({field, query: ''});
        const mdmDataTypeJSON = JSON.parse(body.mdmDataTypeJSON);
        const dataTypeName = mdmDataTypeJSON.UnderlyingDataType.split('.')[1];

        const existingOptions = JSON.parse(JSON.stringify( this.field.data.options));

        let firstOptions;

        if (dataTypeName === 'Decimal') {
          const scale = mdmDataTypeJSON.Scale;
          firstOptions = [...body.data.map(item => ({
            id: item.recordGuid,
            text: Number.parseFloat(item.value).toFixed(scale),
          }))];
        } else {
          firstOptions = [...body.data.map(item => ({
            id: item.recordGuid,
            text: item.value,
          }))];
        }

        const combinedOptions = _.uniqBy(existingOptions.concat(firstOptions), 'id');
        // .slice(0, 50);

        let sortedOptions;

        if (dataTypeName === 'Decimal') {
          sortedOptions = combinedOptions.sort((a, b) => a.text - b.text);
        } else {
          sortedOptions = combinedOptions.sort((a, b) => this.intl.compare(a.text, b.text));
          const lastElement = sortedOptions[sortedOptions.length - 1];
          // sortedOptions.unshift({...lastElement, text: '<EMPTY>'});
        }

        this.field.data.options = JSON.parse(JSON.stringify(sortedOptions));
        this.isLookupFieldValuesLoading = false;
      }
      const suggestionName = item[`${field.name}__$LookupStatus`];
      if (this.isValidSuggestions(suggestionName)) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        suggestions.forEach(suggestion => {
          const recordGuid = suggestion.RecordGuid || suggestion.recordGuid;
          const multiselectOption = document.getElementById(`multiselect-option-${recordGuid}`);
          const multiselectValue = suggestionName.options.find(op => op.id === recordGuid);
          if (multiselectOption && multiselectValue) {
            const parentElement = multiselectOption.parentElement;
            parentElement.style.backgroundColor = multiselectValue.backgroundColor;
            parentElement.addEventListener('mouseover', () => {
              parentElement.style.backgroundColor = multiselectValue.id === this.model[index].id ? '#ff6a6a' : '#41b883';
            });
            parentElement.addEventListener('mouseleave', () => {
              parentElement.style.backgroundColor = multiselectValue.backgroundColor;
            });
          }
        });
      }

      this.$emit('multi-select-open');
    },
    isValidSuggestions(suggestionName) {
      if (suggestionName) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        return suggestions && suggestions.length > 0;
      }
      return false;
    },
    setHierarchyValue(value, level) {
      this.displaySetMessage = true;
      if (this.isHierarchyList()) {
        //on double click remove chosen item
        if (this.model[this.attribute][level] === value && value !== null) {
          value = null;
        }
        this.model[this.attribute][level] = value;
      } else if (this.isHierarchySelect()) {
        this.model[this.attribute][level] = value;
      }
      this.getHierarchyOptions(level);
      this.displaySetMessage = false;
    },
    setParent(level) {
      //base case
      if (level <= 0) {
        return;
      }

      if (this.isHierarchyList()) {
        const currentItem = this.options.optionByLevel[level].find((item) => item[this.options.hierarchyField.primary.name] === this.model[this.attribute][level]);
        const parentItem = this.options.optionByLevel[level - 1].find((item) => item[this.options.hierarchyField.primary.name] === currentItem[this.options.hierarchyField.parent.name]);
        this.model[this.attribute][level - 1] = parentItem[this.options.hierarchyField.primary.name];
      } else if (this.isHierarchySelect()) {
        const currentItem = this.options.optionByLevel[level].find((item) => item[this.options.hierarchyField.primary.name] === this.model[this.attribute][level][this.options.hierarchyField.primary.name]);
        this.model[this.attribute][level - 1] = this.options.optionByLevel[level - 1].find((item) => item[this.options.hierarchyField.primary.name] === currentItem[this.options.hierarchyField.parent.name]);
      }

      this.setParent(level - 1);
    },
    getFormattedDate() {
      const content = this.customDateChange.inputDate;
      const defaultFormat = 'YYYY-MM-DD';

      return this.isDate(this.field) ?
        this.$options.filters.toDate(moment(content, this.dateFormat).toDate(), defaultFormat) :
        this.$options.filters.toDatetime(moment(content, this.dateFormat).toDate(), `${defaultFormat} HH:mm:ss`);
    },
    getHierarchyOptions(changedLevel) {
      //If user chooses item without setting parent. use recursive call to set parents
      if (!this.model[this.attribute][changedLevel - 1]) {
        this.setParent(changedLevel);
      }

      changedLevel = parseInt(changedLevel);
      let latestItem = this.options.data;

      //Needs to re-render views
      for (let i = 0; i <= changedLevel; i++) {
        this.options.optionByLevel[i] = [];
      }

      //Get latest child's current item
      for (let i = 0; i <= changedLevel; i++) {
        this.options.optionByLevel[i] = latestItem;
        let foundItem;
        if (this.model[this.attribute][i] && this.isHierarchySelect()) {
          foundItem = latestItem.find((elem) => this.model[this.attribute][i][this.options.hierarchyField.primary.name] === elem[this.options.hierarchyField.primary.name]);
        } else {
          foundItem = latestItem.find((elem) => this.model[this.attribute][i] === elem[this.options.hierarchyField.primary.name]);
        }
        latestItem = foundItem && foundItem.children ? foundItem.children : [];
      }

      //Reset values for above current level
      for (const level in this.options.levels) {
        if (level > changedLevel) {
          this.model[this.attribute][level] = null;
          this.options.optionByLevel[level] = [];
        }
      }


      //Set options for child level
      if (this.model[this.attribute].hasOwnProperty(changedLevel + 1)) {
        this.options.optionByLevel[changedLevel + 1] = latestItem;
      }


      for (const level in this.options.levels) {
        if (level >= changedLevel + 1) {
          //If parent is already selected use his children otherwise display all data
          if (this.model[this.attribute][level - 1]) {
            let foundItem;
            if (this.isHierarchySelect()) {
              foundItem = this.options.optionByLevel[level - 1].find((elem) => this.model[this.attribute][level - 1][this.options.hierarchyField.primary.name] === elem[this.options.hierarchyField.primary.name]);
            } else if (this.isHierarchyList()) {
              foundItem = this.options.optionByLevel[level - 1].find((elem) => this.model[this.attribute][level - 1] === elem[this.options.hierarchyField.primary.name]);
            }

            this.options.optionByLevel[level] = foundItem && foundItem.children ? foundItem.children : [];
          } else {
            this.options.optionByLevel[level - 1].forEach((levelItem) => {
              const list = levelItem.children ? levelItem.children : [];
              this.options.optionByLevel[level] = [...this.options.optionByLevel[level], ...list];
            });
          }
        }
      }


      // Set placeholders
      for (const level in this.options.levels) {
        this.options.optionByLevel[level].sort((a, b) => a[this.config.optionLabel].localeCompare(b[this.config.optionLabel]));

        const placeholder = {disabled: true, $isDisabled: true};
        placeholder[this.config.optionValue] = null;
        if (this.options.optionByLevel[level].length > 0) {
          placeholder[this.config.optionLabel] = `Please select ${this.options.levels[level].levelName}`;
        } else if (this.model[this.attribute][level - 1] && this.options.optionByLevel[level].length === 0) {
          placeholder[this.config.optionLabel] = `No ${this.options.levels[level].levelName} found`;
        } else if (!this.model[this.attribute][level - 1]) {
          placeholder[this.config.optionLabel] = `Please select parent ${this.options.levels[level - 1].levelName}`;
        }
        this.options.optionByLevel[level] = [placeholder, ...this.options.optionByLevel[level]];
      }

    },
    getHierarchyListItems(level) {
      return this.options.optionByLevel[level].filter((elem) => {
        if (!elem[this.config.optionValue]) {
          return false;
        }

        const search = this.hierarchySearch[level] ? this.hierarchySearch[level] : '';

        return elem[this.config.optionLabel].toLowerCase().includes(search.toLowerCase());

      });
    },
    getEmptyMessage(level) {
      const empty = this.options.optionByLevel[level].find((elem) => elem[this.config.optionValue] === null);
      return empty ? empty[this.config.optionLabel] : '';
    },
    onAddNewRelationalEntity(item, field) {
      this.$emit('on-add-new-relational-entity', item, field);
    },
    getTableName(field, item) {
      const suggestionName = item[`${field.name}__$LookupStatus`];
      if (item[field.name] && item[field.name].id === null && this.isValidSuggestions(suggestionName)) {
        const suggestions = suggestionName.Suggestions || suggestionName.suggestions;
        const value = suggestions[0].OriginalValue || suggestions[0].originalValue;
        return `Add "${value}"`;
      }
      // let relationalTable = this.currentTable
      return `Add New ${this.tablesList[field.relationalTableId]?.label}`;
    },
    isInvalidDate(date) {
      return date === 'Invalid date';
    },
    addDatePickerEventListeners(ev) {
      const currentInput = this.$refs[`currentInput-${this.attribute}`];

      this.customDateChange.isInvalidDate = false;
      this.hasFocus = false;

      ev.target.addEventListener('input', () => {
        this.customDateChange.isInvalidDate = false;
        this.hasFocus = false;

        this.customDateChange = {
          ...this.customDateChange,
          inputEdit: true,
          inputDate: currentInput.text,
          [`open_${this.attribute}`]: false,
        };
      });

      ev.target.addEventListener('click', () => {
        // Update model value after change from input
        if (this.customDateChange.inputEdit) {
          const date = this.getFormattedDate();
          if (this.isInvalidDate(date)) {
            currentInput.blur();
            currentInput.focus();
          } else {
            this.model[this.attribute] = moment(date).format(this.dateFormat);
          }
          this.customDateChange.isInvalidDate = this.isInvalidDate(date);
          this.hasFocus = this.isInvalidDate(date);
        }

        this.customDateChange = {
          ...this.customDateChange,
          inputEdit: false,
          inputDate: '',
          [`open_${this.attribute}`]: true,
        };
      });

      ev.target.addEventListener('blur', () => {
        if (this.customDateChange.inputEdit) {
          const date = this.getFormattedDate();
          this.model[this.attribute] = this.isInvalidDate(date) ? null : moment(date).format(this.dateFormat);
          this.customDateChange.isInvalidDate = this.isInvalidDate(date);
          this.hasFocus = this.isInvalidDate(date);
        }
      });
    },
    initialWidgetData() {
      this.attribute = this.field.name;
      this.rules = this.skipValidationRules ? '' : this.getRules();
      this.label = this.field.label || this.field.displayName;
      if (this.field.labelPosition) {
        this.labelPosition = this.field.labelPosition;
      }
      this.placeholder = this.field.placeholder || this.field.label || this.field.displayName;
      this.hint = this.field.hint || '';
      this.type = this.field.type || this.field.dataTypeName.toLowerCase();
      
      // For document fields, ensure we use the correct type
      if (this.field.dataTypeName === 'document') {
        this.type = 'document';
      }
      
      this.config = this.field.config;

      if ([
        'select',
        'radio-group',
        'multi-select',
        'hierarchy-select',
        'hierarchy-list',
        'hierarchy-tree',
        'custom-select',
      ].includes(this.field.type)) {
        this.options = this.field.data.options;
      }

      // Initialise document field with proper array structure
      if (this.isDocument() && this.model[this.attribute]) {
        if (Array.isArray(this.model[this.attribute])) {
          this.documentFiles = this.model[this.attribute];
        } else {
          try {
            const parsed = JSON.parse(this.model[this.attribute]);
            this.documentFiles = Array.isArray(parsed) ? parsed : [parsed];
          } catch (error) {
            this.documentFiles = [];
          }
        }
      }
    },
    
    // Sort validation warnings and data quality issues into separate arrays
    sortValidationWarnings() {
      this.validationWarnings = [];
      this.dataQualityIssues = [];
      
      if (!this.model || !this.model.failedFields) return;

      for (const f of this.model.failedFields) {
        if (f.fields.includes(this.field.fieldGuid)) {
          if (f.type === 'warning') this.validationWarnings.push(f);
          else if (f.type === 'dataQuality') this.dataQualityIssues.push(f);
        }
      }
    },
  },
  async mounted() {
    this.intl = new Intl.Collator();
    this.backupField = JSON.parse(JSON.stringify(this.field));
    this.initialWidgetData();
    this.sortValidationWarnings();
    if (['hierarchy-select', 'hierarchy-list'].includes(this.field.type)) {
      let highestExistingLevel = 0;
      for (const index in this.model[this.attribute]) {
        if (!this.model[this.attribute][index]) {
          break;
        }
        highestExistingLevel = index;
      }
      this.getHierarchyOptions(highestExistingLevel, this.options.levels[highestExistingLevel]);
    }

    if (['multi-select'].includes(this.field.type) && this.field.data.searchChange) {
      this.searchChange = this.field.data.searchChange.bind(this);
    }
    if(this.rls){
      const ids = JSON.parse(this.model[this.field.name]);
      if(ids && ids.length){
        var result = await this.getUsersAndGroupsForIds(ids);
        this.rlsUsers = result.body;
      }
      else{
        this.rlsUsers=[];
      }
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../scss/variables";

@media (max-width: map-get($grid-breakpoints, 'sm')) {
  .input-label {
    text-align: left;
  }
}

.hierarchy-list-group {
  min-height: 300px;
  max-height: 300px;
  overflow: auto;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.list-group-item {
  border-left: none;
  border-right: none;
  padding: 0.25rem 0.25rem;
}

.list-group-item:hover {
  cursor: pointer;
}

.message-container {
  justify-content: center;
  align-items: center;
  flex: 1;
  flex-direction: column;
  display: flex;
}

.centered-message {
  color: grey;
}

.disabled {
  opacity: 0.7;
  cursor: not-allowed;
  color: #7b8a8b;
}

.datepicker-w {
  width: 100% !important;
}

/deep/ .mx-input-wrapper {

  .datepicker-validation {
    border-color: #dc3545;

    &:focus {
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    }
  }
}

.select-with-filter-group {
  margin-top: -16px;
}

/deep/ .vs__dropdown-toggle {
  padding: 0 0 7px 0 !important;

  .vs__selected {
    display: ruby;
    max-width: 128px;
    overflow: hidden;
    white-space: nowrap;
  }

  .vs__selected-options {
    height: 25px;
  }
}

.form-control-warning {
  border: 1px solid #cd7f00 !important;

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(155, 93, 26, .25)
  }
}

.warning-feedback {
  color: #cd7f00 !important;
}
.rls{
  display: flex;
  flex: 1;
  padding: 8px;
  border: 1px solid #ced4da;
  border-radius: 5px;
  cursor: pointer;
}

.document-input-div {
  flex-grow: 1;
  padding: 0.25rem 0.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  background-color: #e9ecef;
  color: #6c757d;
  margin-right: 0.5rem;
  min-height: calc(1.5em + 0.75rem + 2px);
}

.document-badge {
  font-size: 0.75rem !important;
  font-weight: normal;
  line-height: normal;
  max-width: 200px;
  overflow: hidden;
}

.file-name-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}
</style>
